import { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2 } from 'lucide-react';

const TeamManager = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMembers = async () => {
        try {
            const q = query(collection(db, "team"), orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMembers(data);
        } catch (error) {
            console.error("Error fetching team:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Bu üyeyi silmek istediğinize emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "team", id));
                setMembers(members.filter(item => item.id !== id));
            } catch (error) {
                console.error("Error deleting member:", error);
                alert("Silme işlemi başarısız oldu.");
            }
        }
    };

    if (loading) return <div className="text-white">Yükleniyor...</div>;

    const getCategoryLabel = (cat) => {
        switch (cat) {
            case 'baskan': return 'Topluluk Başkanı';
            case 'yonetim': return 'Yönetim Kurulu';
            case 'denetim': return 'Denetim Kurulu';
            default: return cat;
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-white">Ekip Yönetimi</h1>
                <Link
                    to="/admin/team/new"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <Plus size={20} />
                    <span>Yeni Ekle</span>
                </Link>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <table className="w-full text-left text-gray-400 hidden md:table">
                    <thead className="bg-gray-700/50 text-gray-200 uppercase text-xs">
                        <tr>
                            <th className="px-6 py-4">İsim</th>
                            <th className="px-6 py-4">Görev</th>
                            <th className="px-6 py-4">Kategori</th>
                            <th className="px-6 py-4 text-right">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {members.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                    Henüz hiç üye eklenmemiş.
                                </td>
                            </tr>
                        ) : (
                            members.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-700/30 transition-colors">
                                    <td className="px-6 py-4 font-medium text-white flex items-center gap-3">
                                        {item.image && (
                                            <img src={item.image} alt={item.name} className="w-8 h-8 rounded-full object-cover" />
                                        )}
                                        {item.name || <span className="text-red-400 italic">(İsim Girilmemiş)</span>}
                                    </td>
                                    <td className="px-6 py-4">{item.role}</td>
                                    <td className="px-6 py-4 text-cyan-400 text-sm">{getCategoryLabel(item.category)}</td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-3">
                                        <Link
                                            to={`/admin/team/edit/${item.id}`}
                                            className="text-blue-400 hover:text-blue-300 p-1 rounded-md hover:bg-blue-400/10 transition-colors"
                                        >
                                            <Edit2 size={18} />
                                        </Link>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="text-red-400 hover:text-red-300 p-1 rounded-md hover:bg-red-400/10 transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="md:hidden flex flex-col divide-y divide-gray-700">
                    {members.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            Henüz hiç üye eklenmemiş.
                        </div>
                    ) : (
                        members.map((item) => (
                            <div key={item.id} className="p-4 flex gap-4 items-center">
                                {/* Auto-Avatar (Left) */}
                                <div className="flex-shrink-0">
                                    {item.image ? (
                                        <img src={item.image} alt={item.name} className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500/30" />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 border-2 border-transparent">
                                            <Plus size={20} className="rotate-45" />
                                        </div>
                                    )}
                                </div>

                                {/* Content (Middle) */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-white font-bold truncate text-lg mb-1">
                                        {item.name || <span className="italic text-red-400">(İsimsiz)</span>}
                                    </h3>
                                    <div className="text-gray-400 text-sm flex flex-col">
                                        <span className="text-cyan-400 font-medium">{item.role}</span>
                                        <span className="text-xs opacity-70">{getCategoryLabel(item.category)}</span>
                                    </div>
                                </div>

                                {/* Actions (Right) */}
                                <div className="flex flex-col gap-2">
                                    <Link
                                        to={`/admin/team/edit/${item.id}`}
                                        className="p-3 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500 hover:text-white transition-colors"
                                    >
                                        <Edit2 size={20} />
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(item.id)}
                                        className="p-3 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamManager;
